import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, CheckCircle2, Download } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// 5 questions, each option maps to a stage key. Majority vote → stage.
const QUESTIONS = [
  {
    id: "q1_status",
    prompt: "Kde ste dnes s vaším stavebným projektom?",
    options: [
      { label: "Snívam o vlastnom bývaní, hľadám inšpirácie", value: "dreaming" },
      { label: "Zbieram cenové ponuky, porovnávam firmy", value: "searching" },
      { label: "Mám firmu, riešime projekt / povolenia", value: "permitting" },
      { label: "Stavia sa — sme na stavbe", value: "building" },
      { label: "Dokončujeme, chystáme kolaudáciu", value: "finishing" },
    ],
  },
  {
    id: "q2_budget",
    prompt: "Aký je stav vášho rozpočtu?",
    options: [
      { label: "Ešte len počítam, čo si môžem dovoliť", value: "dreaming" },
      { label: "Mám odhad, ale potrebujem presnejšie ceny", value: "searching" },
      { label: "Rozpočet je schválený, riešim úver / dotácie", value: "permitting" },
      { label: "Priebežne platím faktúry firme", value: "building" },
      { label: "Zvyšné financie na dokončovačky", value: "finishing" },
    ],
  },
  {
    id: "q3_land",
    prompt: "Ako to máte s pozemkom / nehnuteľnosťou?",
    options: [
      { label: "Ešte hľadám / nemám", value: "dreaming" },
      { label: "Vytipovaný, čakám na kúpu alebo prieskum", value: "searching" },
      { label: "Vlastním, riešim geologický posudok a projekt", value: "permitting" },
      { label: "Základy sú, stavia sa hrubá stavba", value: "building" },
      { label: "Hotové, ide o interiér a dokončovačky", value: "finishing" },
    ],
  },
  {
    id: "q4_permit",
    prompt: "Kde ste s povoleniami a projektom?",
    options: [
      { label: "Zatiaľ som sa tomu nevenoval(a)", value: "dreaming" },
      { label: "Zisťujem, čo všetko potrebujem", value: "searching" },
      { label: "Podávam žiadosť alebo mám čerstvé povolenie", value: "permitting" },
      { label: "Stavebný denník sa vedie, kontroly bežia", value: "building" },
      { label: "Chystám kolaudačné konanie", value: "finishing" },
    ],
  },
  {
    id: "q5_urgency",
    prompt: "Kedy chcete/potrebujete mať hotovo?",
    options: [
      { label: "Nemám presný termín, plánujem dlhodobo", value: "dreaming" },
      { label: "Do 12 mesiacov by som chcel(a) začať", value: "searching" },
      { label: "Do 6 mesiacov musím mať povolenia", value: "permitting" },
      { label: "Sme v strede procesu, sledujem harmonogram", value: "building" },
      { label: "Chcem sa čo najskôr sťahovať", value: "finishing" },
    ],
  },
];

export default function JourneyQuiz() {
  const [started, setStarted] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // { q1_status: 'dreaming', ... }
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [error, setError] = useState("");

  const totalSteps = QUESTIONS.length;
  const progress = Math.round(((stepIdx + (result ? 1 : 0)) / (totalSteps + 1)) * 100);

  const currentQ = QUESTIONS[stepIdx];
  const canGoNext = currentQ && answers[currentQ.id];

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        answers: Object.entries(finalAnswers).map(([question_id, answer_value]) => ({
          question_id,
          answer_value,
        })),
      };
      const { data } = await axios.post(`${API}/api/quiz/submit`, payload);
      setResult(data);
    } catch (e) {
      setError("Nepodarilo sa uložiť odpovede. Skúste znova.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectOption = (value) => {
    const nextAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(nextAnswers);
    // Auto-advance after 200ms for smoother UX
    setTimeout(() => {
      if (stepIdx < totalSteps - 1) {
        setStepIdx(stepIdx + 1);
      } else {
        submit(nextAnswers);
      }
    }, 220);
  };

  const restart = () => {
    setStarted(false);
    setStepIdx(0);
    setAnswers({});
    setResult(null);
    setEmail("");
    setEmailSaved(false);
  };

  const saveEmail = async () => {
    if (!email || !email.includes("@")) return;
    try {
      await axios.post(`${API}/api/quiz/submit`, {
        answers: Object.entries(answers).map(([question_id, answer_value]) => ({
          question_id,
          answer_value,
        })),
        email,
      });
      setEmailSaved(true);
    } catch {
      setError("Email sa nepodarilo uložiť. Skúste znova.");
    }
  };

  return (
    <section
      id="kviz"
      className="py-20 bg-gradient-to-b from-[#0f0f0f] via-[#0a0a0a] to-[#0f0f0f]"
      data-testid="journey-quiz-section"
    >
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5E00] font-black mb-3">
            Emocionálny sprievodca · Zdarma · 60 sekúnd
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Kde ste <span className="text-[#00E5FF] neon-cyan">v procese</span>?
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg mt-4 max-w-xl mx-auto">
            Odpovedzte na 5 rýchlych otázok — zistíme, v ktorej fáze ste, a pripravíme vám
            personalizovaný check-list ďalších krokov.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/40 p-6 sm:p-10 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF5E00]/5 via-transparent to-[#00E5FF]/5 pointer-events-none" />

          {!started && !result && (
            <div className="relative text-center py-6" data-testid="quiz-intro">
              <div className="w-16 h-16 rounded-2xl bg-[#FF5E00]/15 border border-[#FF5E00]/40 flex items-center justify-center mx-auto mb-6">
                <Sparkles size={28} className="text-[#FF5E00]" />
              </div>
              <p className="text-zinc-300 text-lg mb-6 max-w-md mx-auto">
                Poradíme vám presne to, čo potrebujete práve <em>vy</em>. Bez zbytočností.
              </p>
              <button
                onClick={() => setStarted(true)}
                className="btn-primary inline-flex items-center gap-3"
                data-testid="quiz-start-btn"
              >
                Začať kvíz <ArrowRight size={18} />
              </button>
            </div>
          )}

          {started && !result && currentQ && (
            <div className="relative">
              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
                  <span className="mono uppercase tracking-widest">
                    Otázka {stepIdx + 1} / {totalSteps}
                  </span>
                  <span>{progress}%</span>
                </div>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF5E00] to-[#00E5FF] transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQ.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  <h3 className="text-2xl sm:text-3xl font-bold mb-6 leading-tight">
                    {currentQ.prompt}
                  </h3>
                  <div className="grid gap-3">
                    {currentQ.options.map((opt) => {
                      const selected = answers[currentQ.id] === opt.value;
                      return (
                        <button
                          key={opt.value + opt.label}
                          onClick={() => selectOption(opt.value)}
                          className={`text-left px-5 py-4 rounded-xl border transition-all group ${
                            selected
                              ? "border-[#00E5FF] bg-[#00E5FF]/10"
                              : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20"
                          }`}
                          data-testid={`quiz-option-${opt.value}`}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <span className={`text-sm sm:text-base ${selected ? "text-white" : "text-zinc-300"}`}>
                              {opt.label}
                            </span>
                            {selected && <CheckCircle2 size={18} className="text-[#00E5FF] flex-shrink-0" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                <button
                  onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
                  disabled={stepIdx === 0}
                  className="text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="quiz-back-btn"
                >
                  <ArrowLeft size={16} /> Späť
                </button>
                <span className="text-xs text-zinc-500">
                  {canGoNext ? "Automaticky pokračuje…" : "Vyberte odpoveď"}
                </span>
              </div>
            </div>
          )}

          {submitting && (
            <div className="relative text-center py-10" data-testid="quiz-submitting">
              <div className="animate-spin w-10 h-10 border-2 border-[#00E5FF] border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-sm text-zinc-400">Vyhodnocujem odpovede…</p>
            </div>
          )}

          {result && (
            <div className="relative" data-testid="quiz-result">
              <div className="text-center mb-8">
                <div className="text-5xl mb-3" aria-hidden="true">{result.emoji}</div>
                <div className="text-xs uppercase tracking-widest text-[#00E5FF] font-black mb-2">
                  Vaša fáza
                </div>
                <h3 className="text-3xl sm:text-4xl font-black tracking-tight">
                  {result.title}
                </h3>
                <p className="text-zinc-400 mt-4 max-w-xl mx-auto text-base leading-relaxed">
                  {result.description}
                </p>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-6">
                <h4 className="text-sm uppercase tracking-widest text-[#FF5E00] font-black mb-4">
                  Vaše ďalšie kroky
                </h4>
                <ul className="space-y-3">
                  {result.next_steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-zinc-200">
                      <span className="w-6 h-6 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center flex-shrink-0 text-[#00E5FF] font-black text-xs mt-0.5">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <a
                  href={result.cta_url}
                  className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  data-testid="quiz-cta-btn"
                >
                  <Sparkles size={18} />
                  {result.cta_label}
                </a>
                <button
                  onClick={() => window.print()}
                  className="btn-secondary flex-1 inline-flex items-center justify-center gap-2"
                  data-testid="quiz-print-btn"
                >
                  <Download size={18} /> Uložiť ako PDF
                </button>
              </div>

              {!emailSaved ? (
                <div className="bg-black/40 rounded-xl p-4 border border-white/10">
                  <p className="text-sm text-zinc-300 mb-3">
                    Chcete tento check-list poslať do e-mailu? (nezasielame spam)
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vas@email.sk"
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00E5FF]"
                      data-testid="quiz-email-input"
                    />
                    <button
                      onClick={saveEmail}
                      className="px-5 py-3 bg-[#00E5FF] hover:brightness-110 text-black font-black rounded-lg text-sm whitespace-nowrap"
                      data-testid="quiz-email-submit"
                    >
                      Poslať
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-[#00E5FF]/10 border border-[#00E5FF]/40 rounded-xl p-4 text-sm text-[#00E5FF]" data-testid="quiz-email-saved">
                  Ďakujeme! Zaznamenali sme vaše údaje — ozveme sa s ďalšími krokmi.
                </div>
              )}

              <button
                onClick={restart}
                className="text-xs text-zinc-500 hover:text-zinc-300 mt-6 mx-auto block underline"
                data-testid="quiz-restart-btn"
              >
                Spustiť kvíz znova
              </button>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-400 mt-4" data-testid="quiz-error">{error}</p>
          )}
        </div>
      </div>
    </section>
  );
}
